import classNames from 'classnames';
import React, { MouseEventHandler, ReactElement, SVGProps, useMemo } from 'react';
import ActionableIcon, { IActionableIconProps } from '../ActionableIcon/ActionableIcon';
import ActionableMenuIcon, {
  IActionableMenuIconProps,
} from '../ActionableMenuIcon/ActionableMenuIcon';
import useStyles from './Icon.styles';

export type TActionable = boolean | IActionableMenuIconProps | IActionableIconProps;

export interface IIconProps extends Omit<SVGProps<SVGSVGElement>, 'onClick'> {
  /**
   * If true show ActionableIcon with BareIcon inside unless its an object with menuChoices
   * in which case show an ActionableMenuIcon
   */
  actionable?: TActionable;
  /** Function run when icon is clicked */
  onClick?: MouseEventHandler;
  /** Name of the icon, almost always matches the name of the svg file it came from */
  name: string;
  /** Svgz generated icon component that has the full svg tag built right into the component */
  SvgIcon: (props: SVGProps<SVGSVGElement>) => ReactElement;
  /** Title to set for the icon, this will inject a title attribute into the svg tag */
  title?: string;
  /** Allows you to add additional class names to the icon wrapper div, but
   *  if noIconWrapper is true it will add the class names to the svg icon
   */
  className?: string;
  /** Fill of the svg if the default is not desired, should be a legal CSS color */
  fill?: string;
  /** Height of the svg icon itself, should be a legal CSS unit */
  height?: string | number;
  /** Width of the svg icon itself, should be a legal CSS unit */
  width?: string | number;
  /** Optional text to show in a sibling span to the svg tag */
  text?: string;
  /** True if you want the text to be visible */
  textVisible?: boolean;
  /** Removes the div wrapper from the icon. The wrapper has display: 'inline-block'
   * and the className prop applied to it. */
  noIconWrapper?: boolean;
}

const getActionableSpreadProps = (actionable: boolean | IActionableIconProps) =>
  typeof actionable === 'object' ? actionable : {};

const Icon = (props: IIconProps) => {
  const {
    SvgIcon,
    actionable,
    className,
    fill,
    height = 16,
    name,
    noIconWrapper,
    onClick,
    text,
    textVisible,
    width = 16,
    ...svgProps
  } = props;
  const classes = useStyles(useMemo(() => ({ height, width, fill }), [height, width, fill]));

  const bareIcon = (
    <SvgIcon
      className={classNames(`icon-${name}`, classes.icon, noIconWrapper && className)}
      onClick={actionable ? undefined : onClick}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...svgProps}
    />
  );

  if (actionable) {
    // ActionableMenuIcon should only be displayed if actionable is an object and has the menuChoices prop.
    return actionable !== true && actionable.menuChoices ? (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <ActionableMenuIcon className={className} {...actionable}>
        {bareIcon}
      </ActionableMenuIcon>
    ) : (
      // Since actionable can be true or an object, in this case, we need to check it is an object before spreading.
      <ActionableIcon
        className={className}
        onClick={onClick}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...getActionableSpreadProps(actionable)}
      >
        {bareIcon}
      </ActionableIcon>
    );
  }

  return noIconWrapper ? (
    bareIcon
  ) : (
    <div className={classNames(classes.iconWrapper, className)}>
      {bareIcon}
      {text ? <span className={textVisible ? '' : 'sr-only'}>{text}</span> : ''}
    </div>
  );
};

const StyledIcon = Icon;

/**
 * Creates an Icon component with the provided title so it doesn't need to be provided later at the
 * point of use.
 * */
export function withTitle(IconComponent: typeof Icon, title: string) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  return (props: IIconProps) => <IconComponent {...props} title={title} />;
}

export const IconBase = Icon;

/**
 * Provides svg icon components with optional light html wrapping and JSS styling/theming support.
 *
 * Note: You may pass any valid svg attribute as a prop and if it is not handled directly by this
 * component it will be added to the svg element itself.
 * One very helpful attribute is focusable={false} which will prevent IE and Edge from allowing the
 * svg to get focus and mess up your tab order when you are using svg's inside button elements and
 * wish the button to be the element tabbed to.
 */
export default StyledIcon;
